import { hexToRgba } from '@/shared/lib/helpers/hex-to-rgba.helper';

import * as RADIUS from '../../styles/radiuses';
import * as COLOR from './colors';
import { yachtTheme as theme } from './theme';

export const yachtDarkTheme = {
	colors: {
		primary: COLOR.WHITE,
		secondary: COLOR.NAVY_7,
		tertiary: COLOR.GRAY_1,
		body: COLOR.NAVY_5,
		metaColor: COLOR.DARK_2,
		...theme.colors,
		error: COLOR.RED_4,
	},
	input: {
		primary: {
			border: COLOR.NAVY_6,
			fontColor: COLOR.NAVY_3,
			placeholderColor: COLOR.NAVY_3,
			background: hexToRgba(COLOR.WHITE, 0.05),
			focus: {
				border: COLOR.NAVY_2,
				color: COLOR.WHITE,
				placeholderColor: COLOR.WHITE,
			},
			filled: {
				color: COLOR.WHITE,
			},
			disabled: {
				border: hexToRgba(COLOR.WHITE, 0),
				color: hexToRgba(COLOR.WHITE, 0.3),
				placeholderColor: hexToRgba(COLOR.WHITE, 0.3),
			},
			error: {
				border: COLOR.RED_2,
				background: COLOR.RED_0,
				color: COLOR.RED_4,
				placeholderColor: COLOR.RED_4,
			},
			radius: RADIUS.PX_8,
		},
		secondary: {
			border: COLOR.NAVY_6,
			fontColor: COLOR.NAVY_3,
			placeholderColor: COLOR.NAVY_3,
			background: hexToRgba(COLOR.WHITE, 0.05),
			focus: {
				border: COLOR.NAVY_2,
				color: COLOR.WHITE,
				placeholderColor: COLOR.WHITE,
			},
			filled: {
				color: COLOR.WHITE,
			},
			disabled: {
				border: hexToRgba(COLOR.WHITE, 0),
				color: hexToRgba(COLOR.WHITE, 0.3),
				placeholderColor: hexToRgba(COLOR.WHITE, 0.3),
			},
			error: {
				border: COLOR.RED_2,
				background: COLOR.RED_0,
				color: COLOR.RED_4,
				placeholderColor: COLOR.RED_4,
			},
			radius: RADIUS.PX_8,
		},
		...theme.input,
	},
	textarea: {
		primary: {
			border: COLOR.NAVY_6,
			fontColor: COLOR.NAVY_3,
			placeholderColor: COLOR.NAVY_3,
			background: hexToRgba(COLOR.WHITE, 0.05),
			focus: {
				border: COLOR.NAVY_2,
				color: COLOR.WHITE,
				placeholderColor: COLOR.WHITE,
			},
			filled: {
				color: COLOR.WHITE,
			},
			radius: RADIUS.PX_8,
		},
		secondary: {
			border: COLOR.NAVY_6,
			fontColor: COLOR.NAVY_3,
			placeholderColor: COLOR.NAVY_3,
			background: hexToRgba(COLOR.WHITE, 0.05),
			focus: {
				border: COLOR.NAVY_2,
				color: COLOR.WHITE,
				placeholderColor: COLOR.WHITE,
			},
			filled: {
				color: COLOR.WHITE,
			},
			radius: RADIUS.PX_8,
		},
		...theme.textarea,
	},
	box: {
		border: COLOR.GRAY_1,
		background: COLOR.DARK_6,
	},
	checkbox: {
		background: hexToRgba(COLOR.WHITE, 0.05),
		border: COLOR.NAVY_6,
		checked: {
			background: COLOR.NAVY_9,
			border: COLOR.NAVY_9,
		},
	},
	logo: {
		fill: COLOR.DARK_3,
		stroke: COLOR.GRAY_1,
	},
	page: {
		...theme.page,
		wrapper: {
			background: COLOR.DARK_4,
			border: `1px solid ${COLOR.GRAY_1}`,
		},
	},
	text: {
		size: {
			...theme.text.size,
		},
		colors: {
			primary: COLOR.WHITE,
			secondary: COLOR.NAVY_4,
			tertiary: COLOR.NAVY_3,
			invertedPrimary: '#080D17',
			invertedSecondary: COLOR.NAVY_10,
			invertedTertiary: '#3A6DAE',
			inherit: 'inherit',
			...theme.colors,
		},
	},
	chip: {
		primary: {
			background: COLOR.DARK_3,
			borderColor: COLOR.NAVY_8,
			color: COLOR.NAVY_4,
			padding: '7px 12px',
			borderRadius: '10px',
		},
		secondary: {
			background: COLOR.NAVY_10,
			borderColor: COLOR.NAVY_2,
			color: COLOR.NAVY_3,
			padding: '5px 10px',
			borderRadius: '999px',
		},
	},
	download: {
		hover: COLOR.DARK_5,
	},
	button: {
		...theme.button,
		error: {
			background: COLOR.RED_1,
			border: '1px solid transparent',
			color: COLOR.RED_3,

			'&:hover': {
				background: COLOR.RED_2,
				color: COLOR.RED_3,
			},

			'&:active': {
				background: COLOR.RED_2,
				color: COLOR.RED_3,
			},

			'&:disabled': {
				background: hexToRgba(COLOR.WHITE, 0.05),
				border: `1px solid ${hexToRgba(COLOR.WHITE, 0)}`,
				color: hexToRgba(COLOR.WHITE, 0.3),
				cursor: 'initial',
			},
		},
		contained: {
			background: COLOR.NAVY_2,
			border: '1px solid transparent',
			color: COLOR.WHITE,

			'&:hover': {
				background: COLOR.NAVY_1,
			},

			'&:active': {
				background: COLOR.NAVY_1,
			},

			'&:disabled': {
				background: hexToRgba(COLOR.WHITE, 0.05),
				border: `1px solid ${hexToRgba(COLOR.WHITE, 0)}`,
				['pointer-events']: 'none',
				color: hexToRgba(COLOR.WHITE, 0.3),
			},
		},
		outlined: {
			background: 'transparent',
			color: COLOR.NAVY_4,
			border: `1px solid ${COLOR.NAVY_4}`,

			'&:hover': {
				background: 'transparent',
				color: COLOR.NAVY_3,
				border: `1px solid ${COLOR.NAVY_3}`,
			},

			'&:active': {
				background: 'transparent',
				color: COLOR.NAVY_3,
				border: `1px solid ${COLOR.NAVY_3}`,
			},

			'&:disabled': {
				background: hexToRgba(COLOR.WHITE, 0.05),
				border: `1px solid ${hexToRgba(COLOR.WHITE, 0)}`,
				['pointer-events']: 'none',
				color: hexToRgba(COLOR.WHITE, 0.3),
			},
		},
		tertiary: {
			background: 'transparent',
			color: COLOR.WHITE,
			border: `1px solid ${hexToRgba(COLOR.WHITE, 0)}`,

			'&:hover': {
				background: hexToRgba(COLOR.WHITE, 0.05),
				color: COLOR.WHITE,
				border: `1px solid ${hexToRgba(COLOR.WHITE, 0)}`,
			},

			'&:active': {
				background: 'transparent',
				color: COLOR.WHITE,
				border: `1px solid ${hexToRgba(COLOR.WHITE, 0)}`,
			},

			'&:disabled': {
				background: hexToRgba(COLOR.WHITE, 0.05),
				border: `1px solid ${hexToRgba(COLOR.WHITE, 0)}`,
				['pointer-events']: 'none',
				color: hexToRgba(COLOR.WHITE, 0.3),
			},
		},
	},
	title: {
		colors: {
			primary: COLOR.WHITE,
			secondary: COLOR.DARK_1,
		},
		...theme.title,
	},
	toggle: {
		background: hexToRgba(COLOR.WHITE, 0.05),
		border: COLOR.NAVY_6,
		fill: COLOR.WHITE,
		checked: {
			background: COLOR.NAVY_2,
		},
		moon: {
			color: COLOR.GRAY_10,
			checked: COLOR.NAVY_2,
		},
		sun: COLOR.GRAY_10,
	},
	link: {
		color: COLOR.WHITE,
		active: {
			background: COLOR.NAVY_2,
			hover: hexToRgba(COLOR.WHITE, 0.1),
			colors: {
				primary: COLOR.WHITE,
				secondary: COLOR.WHITE,
			},
		},
	},
	avatar: {
		...theme.avatar,
	},
	layouts: {
		header: {
			...theme.layouts.header,
			background: COLOR.DARK_4,
			borderBottom: `1px solid ${COLOR.GRAY_1}`,
			icon: {
				primary: COLOR.WHITE,
			},
		},
		sidebar: {
			...theme.layouts.sidebar,
			background: COLOR.NAVY_10,
			borderRight: `1px solid ${COLOR.NAVY_10}`,
			borderBottom: `1px solid ${COLOR.NAVY_10}`,
			toggle: {
				fill: COLOR.NAVY_10,
				stroke: COLOR.WHITE,
			},
		},
	},
	isDark: true,
	menu: {
		background: COLOR.DARK_6,
		hover: COLOR.DARK_5,
		border: `1px solid ${COLOR.GRAY_1}`,
	},
	stepper: {
		colors: {
			primary: COLOR.NAVY_8,
			disabled: COLOR.GRAY_7,
		},
		border: {
			primary: COLOR.NAVY_8,
			disabled: COLOR.GRAY_1,
		},
	},
	autocomplete: {
		...theme.autocomplete,
	},
	datepicker: {
		color: {
			primary: COLOR.WHITE,
			disabled: hexToRgba(COLOR.WHITE, 0.3),
			scheme: 'dark',
		},
		selected: { background: COLOR.NAVY_9 },
		hover: hexToRgba(COLOR.WHITE, 0.1),
		...theme.datepicker,
	},
	leaflet: {
		color: {
			primary: COLOR.WHITE,
		},
	},
	option: {
		background: COLOR.NAVY_5,
		hover: hexToRgba(COLOR.WHITE, 0.1),
		border: `1.5px solid ${COLOR.NAVY_6}`,
		color: COLOR.WHITE,
		selectedColor: COLOR.NAVY_12,
	},
	select: {
		fill: {
			default: COLOR.WHITE,
			disabled: hexToRgba(COLOR.WHITE, 0.3),
			error: COLOR.RED_4,
		},
		...theme.select,
	},
	// select: {
	// 	arrow: {
	// 		open: openSelect,
	// 		close: closeSelect,
	// 	},
	// },
	zIndex: {
		...theme.zIndex,
	},
	tabs: {
		default: {
			color: COLOR.GRAY_7,
			activeColor: COLOR.NAVY_8,
			// TODO: Replace the legacy Tabs with Tabs from hdata-ui and remove this temporary page background token
			background: 'var(--hdata-background)',
			activeBackground: 'transparent',
			border: `1px solid ${COLOR.GRAY_1}`,
			...theme.tabs.default,
		},
		pill: {
			color: COLOR.GRAY_7,
			activeColor: COLOR.NAVY_5,
			background: 'transparent',
			activeBackground: COLOR.WHITE,
			border: 'none',
			...theme.tabs.pill,
		},
	},
	table: {
		headColor: COLOR.NAVY_4,
		measurementBackground: COLOR.NAVY_0,
		bodyColor: COLOR.NAVY_8,
		background: COLOR.NAVY_5,
		zebraBackground: hexToRgba(COLOR.WHITE, 0.05),
		headBackground: COLOR.NAVY_0,
		border: `1px solid ${COLOR.NAVY_6}`,
		rowBorder: `1px solid ${COLOR.NAVY_6}`,
		offlineBackground: COLOR.GRAY_1,
		...theme.table,
	},
	toast: {
		background: COLOR.DARK_5,
		closeColor: COLOR.WHITE,
	},
	caption: {
		primary: COLOR.WHITE,
		secondary: COLOR.GRAY_9,
		tertiary: COLOR.WHITE,
		gray: COLOR.GRAY_11,
		dark: COLOR.GRAY_5,
	},
	tag: {
		border: COLOR.NAVY_8,
		radius: RADIUS.PX_8,
		background: 'transparent',
		color: COLOR.NAVY_8,
		...theme.tag,
	},
	list: {
		color: COLOR.WHITE,
	},
	container: {
		border: COLOR.GRAY_1,
		radius: RADIUS.PX_2,
	},
	section: {
		border: COLOR.GRAY_1,
		color: COLOR.GRAY_10,
	},
	breadcrumbs: {
		color: COLOR.NAVY_8,
		background: 'transparent',
		border: `1px solid ${COLOR.NAVY_8}`,
		radius: RADIUS.PX_2,
	},
	transition: {
		...theme.transition,
	},
	modal: {
		border: COLOR.GRAY_1,
		borderInside: COLOR.GRAY_1,
		background: COLOR.DARK_3,
		backgroundActions: COLOR.DARK_6,
		backgroundHeader: COLOR.DARK_5,
		icon: COLOR.WHITE,
		color: COLOR.WHITE,
		radius: RADIUS.PX_8,
	},
	groupManagement: {
		...theme.groupManagement,
		groupCard: {
			...theme.groupManagement.groupCard,
			background: COLOR.GROUP_CARD_BACKGROUND,
			borderColor: COLOR.GROUP_CARD_BORDER,
			headerBackground: COLOR.GROUP_CARD_HEADER,
			rowBackground: COLOR.GROUP_CARD_ROW_BACKGROUND,
			rowBorderColor: COLOR.GROUP_CARD_ROW_BORDER,
			metricChipBackground: COLOR.GROUP_CARD_BACKGROUND,
		},
	},
	pagination: {
		color: COLOR.GRAY_8,
		background: 'transparent',
		border: `1px solid ${COLOR.GRAY_1}`,
		active: {
			background: COLOR.NAVY_2,
			color: COLOR.WHITE,
		},
	},
	widgets: {
		circle: { color: COLOR.NAVY_2, background: COLOR.NAVY_8 },
		arrow: COLOR.WHITE,
		addSection: {
			background: COLOR.DARK_5,
		},
	},
	tooltip: {
		background: COLOR.DARK_4,
		hover: COLOR.DARK_5,
		border: `1px solid ${COLOR.GRAY_1}`,
		color: {
			primary: COLOR.WHITE,
			secondary: COLOR.GRAY_5,
		},
	},
	breakpoints: {
		...theme.breakpoints,
	},
	media: {
		...theme.media,
	},
	radio: {
		border: COLOR.NAVY_6,
		background: COLOR.NAVY_9,
		active: COLOR.WHITE,
	},
	spinner: {
		...theme.spinner,
		color: {
			primary: COLOR.NAVY_2,
			secondary: COLOR.WHITE,
		},
	},
	map: {
		icon: COLOR.WHITE,
	},
	windy: {
		icon: {
			background: COLOR.NAVY_8,
			fill: {
				active: COLOR.DARK_1,
				unactive: COLOR.WHITE,
			},
			...theme.windy.icon,
		},
		buttons: {
			background: COLOR.GRAY_12,
			...theme.windy.buttons,
		},
	},
	notification: {
		icon: {
			backgroundColor: COLOR.NAVY_8,
			activeColor: COLOR.NAVY_2,
		},
		card: {
			activeBackground: 'none',
			defaultBorder: COLOR.DARK_6,
			border: COLOR.NAVY_2,
		},
		menu: {
			icon: COLOR.WHITE,
			mobile: {
				icon: COLOR.WHITE,
			},
		},
	},
	icon: {
		roundWrapper: {
			primary: {
				backgroundColor: COLOR.NAVY_8,
				fill: COLOR.NAVY_2,
			},
			secondary: {
				backgroundColor: hexToRgba(COLOR.WHITE, 0.1),
				fill: COLOR.WHITE,
			},
		},
	},
	skeleton: {
		gradient: `linear-gradient(to right, ${COLOR.NAVY_1} 0%, ${COLOR.NAVY_2} 50%, ${COLOR.NAVY_3} 100%)`,
		shadow: COLOR.NAVY_8,
	},
	marker: {
		primary: COLOR.BLUE_1,
		secondary: COLOR.GRAY_2,
		error: COLOR.ERROR,
	},
};
